import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  PackageCheck, Truck, Boxes, RotateCcw, Receipt, ShieldCheck,
  Activity, BellRing, ArrowRight, CheckCircle2,
} from "lucide-react";

const FEATURES = [
  {
    icon: PackageCheck,
    title: "Order Management",
    body: "A single-page order pipeline built around one rule: never ship what you don't have.",
    points: [
      "Mark-as-shipped with strict inventory validation",
      "Quantity Problem & Didn't Arrive workflows",
      "Timeline notes on every order",
      "Live summary cards that double as filters",
    ],
  },
  {
    icon: Truck,
    title: "Inbound Receiving",
    body: "Receive shipments the way they actually arrive — partially, and not always in perfect condition.",
    points: [
      "Per-SKU expected vs received with progress bars",
      "Good units auto-increment available stock",
      "Damaged units quarantined, never sellable",
      "Multiple tracking numbers per shipment",
    ],
  },
  {
    icon: Boxes,
    title: "Audited Inventory",
    body: "Stock levels you can defend in front of any client, backed by an immutable transaction log.",
    points: [
      "Every adjustment requires a documented reason",
      "Add, remove, count-correction, damaged & lost types",
      "Low-stock and out-of-stock alerts",
      "Reserved vs available tracked separately",
    ],
  },
  {
    icon: RotateCcw,
    title: "Returns Processing",
    body: "Turn the messiest part of fulfillment into a clean, photographed, auditable flow.",
    points: [
      "Per-line Sellable / Non-Sellable confirmation",
      "Sellable quantities restock automatically",
      "Photo evidence attached to every return",
      "Confirmed returns locked read-only",
    ],
  },
  {
    icon: Receipt,
    title: "Invoicing Summaries",
    body: "Bill your clients on facts, not estimates — straight from operational data.",
    points: [
      "Per-store billing period summaries",
      "Shipped, returned, cancelled & problem counts",
      "One-click CSV export for finance",
    ],
  },
  {
    icon: ShieldCheck,
    title: "Multi-Tenancy & RBAC",
    body: "Run many warehouses on one platform with zero data leakage between tenants.",
    points: [
      "Every query scoped to the warehouse",
      "8 roles enforced server-side on every endpoint",
      "VA agencies limited to assigned stores",
      "Clients get read-only visibility of their own data",
    ],
  },
];

const EXTRAS = [
  { icon: Activity, title: "Activity Log", body: "Every create, update and delete recorded with a previous → new diff." },
  { icon: BellRing, title: "Notifications", body: "Store-scoped alerts for low stock, problems and inbound arrivals." },
];

export default function FeaturesPage() {
  return (
    <div data-testid="features-page">
      <section className="relative pt-36 md:pt-44 pb-16 lp-grid-lines">
        <div className="lp-orb w-[500px] h-[400px] bg-sky-300/50 top-10 -left-40" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-10">
          <p className="text-xs md:text-sm uppercase tracking-[0.3em] text-blue-600 font-bold mb-4">Features</p>
          <h1 className="lp-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-[#0b1530] max-w-3xl leading-[1.05]">
            Everything a 3PL needs. <span className="lp-gradient-text">Nothing it doesn't.</span>
          </h1>
          <p className="mt-7 text-lg md:text-xl text-slate-600 leading-relaxed max-w-2xl">
            Ten operational modules working as one system — from the receiving dock to the client invoice.
          </p>
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 grid grid-cols-1 md:grid-cols-2 gap-6">
          {FEATURES.map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.55, delay: (i % 2) * 0.08 }}
              className="lp-card rounded-2xl border border-slate-200 p-8 lg:p-10 hover:-translate-y-1 hover:shadow-xl hover:shadow-blue-600/15 hover:border-blue-200 transition-all duration-300"
              data-testid={`feature-detail-${i}`}
            >
              <span className="lp-icon-chip mb-6"><f.icon className="text-blue-600" size={25} strokeWidth={1.5} /></span>
              <h2 className="lp-display text-2xl font-semibold tracking-tight text-[#0b1530]">{f.title}</h2>
              <p className="mt-3 text-base text-slate-600 leading-relaxed">{f.body}</p>
              <ul className="mt-6 space-y-3">
                {f.points.map((p) => (
                  <li key={p} className="flex items-start gap-3 text-[15px] text-slate-700">
                    <CheckCircle2 size={18} strokeWidth={2} className="text-blue-600 mt-0.5 shrink-0" />
                    {p}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        <div className="max-w-7xl mx-auto px-5 lg:px-10 mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          {EXTRAS.map((e) => (
            <div key={e.title} className="rounded-2xl border border-slate-200 bg-blue-50/50 p-8 flex items-start gap-5">
              <e.icon className="text-blue-600 shrink-0 mt-1" size={24} strokeWidth={1.5} />
              <div>
                <h3 className="text-lg font-semibold text-[#0b1530]">{e.title}</h3>
                <p className="mt-1.5 text-[15px] text-slate-600 leading-relaxed">{e.body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="py-20 text-center bg-gradient-to-b from-white to-blue-50">
        <h2 className="lp-display text-3xl sm:text-4xl font-semibold tracking-tight text-[#0b1530]">See it in action</h2>
        <Link
          to="/contact"
          className="group mt-8 inline-flex items-center gap-2.5 rounded-full bg-blue-600 hover:bg-blue-700 text-white text-base md:text-lg font-semibold px-9 py-4 transition-colors shadow-xl shadow-blue-600/30"
          data-testid="features-cta-demo"
        >
          Request a Demo
          <ArrowRight size={19} strokeWidth={2} className="transition-transform group-hover:translate-x-1" />
        </Link>
      </section>
    </div>
  );
}
